// src/components/staff/AttendanceReport.jsx
import React, { useState } from 'react';
import { ArrowLeft, Download, Search } from 'lucide-react';
import { styles } from '../../styles/theme';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// ── Helpers ──────────────────────────────────────────────────────────────
// attendance.date is stored as "MM-DD-YYYY" (see attendanceService.js).
// That format sorts correctly as a string ONLY within the same year, which
// is fine here since we're always querying whole months within one year.

const pad2 = (n) => String(n).padStart(2, '0');

const toDateString = (year, month, day) => `${pad2(month)}-${pad2(day)}-${year}`;

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const studentDisplayName = (r) => r.studentName || 'Unknown Student';

// Same rule FrontDesk.jsx uses: anything other than 'kirkland' counts as Redmond
const normalizeLocation = (loc) => (loc === 'kirkland' ? 'kirkland' : 'redmond');

const LOCATION_LABELS = { redmond: 'Redmond', kirkland: 'Kirkland' };

// Preset months — edit/add years here as new summers roll around
const PRESET_MONTHS = [
  { label: 'June 2026', year: 2026, month: 6 },
  { label: 'July 2026', year: 2026, month: 7 },
  { label: 'August 2026', year: 2026, month: 8 },
];

const AttendanceReport = ({ onBack }) => {
  const [selectedMonths, setSelectedMonths] = useState([true, true, true]); // June, July, Aug all on by default
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all'); // 'all' | 'redmond' | 'kirkland'
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');

  const toggleMonth = (idx) => {
    setSelectedMonths((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  // Build one or more [startStr, endStr] ranges to query, given the format constraint
  const buildRanges = () => {
    if (useCustomRange) {
      if (!customStart || !customEnd) return [];
      // customStart/customEnd come from <input type="date"> as YYYY-MM-DD
      const [sy, sm, sd] = customStart.split('-').map(Number);
      const [ey, em, ed] = customEnd.split('-').map(Number);
      if (sy !== ey) {
        // Split at year boundary since string date format doesn't sort across years
        return [
          [toDateString(sy, sm, sd), toDateString(sy, 12, 31)],
          [toDateString(ey, 1, 1), toDateString(ey, em, ed)],
        ];
      }
      return [[toDateString(sy, sm, sd), toDateString(ey, em, ed)]];
    }
    return PRESET_MONTHS.filter((_, i) => selectedMonths[i]).map(({ year, month }) => [
      toDateString(year, month, 1),
      toDateString(year, month, daysInMonth(year, month)),
    ]);
  };

  const runReport = async () => {
    const ranges = buildRanges();
    if (ranges.length === 0) {
      setError('Select at least one month, or fill in a custom date range.');
      return;
    }
    setError('');
    setLoading(true);
    setHasSearched(true);
    try {
      const attendanceRef = collection(db, 'attendance');
      const allDocs = [];
      const seenIds = new Set();

      for (const [start, end] of ranges) {
        const q = query(
          attendanceRef,
          where('date', '>=', start),
          where('date', '<=', end)
        );
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          if (!seenIds.has(d.id)) {
            seenIds.add(d.id);
            allDocs.push({ id: d.id, ...d.data() });
          }
        });
      }

      // Only count actual check-ins — exclude absent/pending records with no checkInTime
      let checkedIn = allDocs.filter((r) => !!r.checkInTime);

      // ── Location resolution ────────────────────────────────────────────
      // checkInStudent() doesn't always save locationId on the attendance doc
      // (only the manual "mark absent" path does). So: use the record's own
      // locationId if present, otherwise fall back to the student's current
      // locationId on the students collection. Note this fallback reflects
      // the student's CURRENT location, which may not match where they were
      // actually checked in on a past date if they've since transferred sites.
      const missingLocationIds = [
        ...new Set(
          checkedIn.filter((r) => !r.locationId && r.studentId).map((r) => r.studentId)
        ),
      ];

      const studentLocationMap = {};
      if (missingLocationIds.length > 0) {
        // Firestore 'in' queries cap at 30 values per query — batch accordingly
        const studentsRef = collection(db, 'students');
        for (let i = 0; i < missingLocationIds.length; i += 30) {
          const batchIds = missingLocationIds.slice(i, i + 30);
          const q = query(studentsRef, where('__name__', 'in', batchIds));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            studentLocationMap[d.id] = normalizeLocation(d.data().locationId);
          });
        }
      }

      checkedIn = checkedIn.map((r) => ({
        ...r,
        resolvedLocation: r.locationId
          ? normalizeLocation(r.locationId)
          : studentLocationMap[r.studentId] || 'redmond',
      }));

      // Sort by check-in time, then student name
      checkedIn.sort((a, b) => {
        const da = new Date(a.checkInTime);
        const dbb = new Date(b.checkInTime);
        if (da - dbb !== 0) return da - dbb;
        return studentDisplayName(a).localeCompare(studentDisplayName(b));
      });

      setRecords(checkedIn);
    } catch (err) {
      console.error('Attendance report error:', err);
      setError(`Failed to load report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const visibleRecords =
    locationFilter === 'all' ? records : records.filter((r) => r.resolvedLocation === locationFilter);

  const totalsByLocation = records.reduce(
    (acc, r) => {
      acc[r.resolvedLocation] = (acc[r.resolvedLocation] || 0) + 1;
      return acc;
    },
    { redmond: 0, kirkland: 0 }
  );

  // Group visible records by "MM-YYYY", then by location within each month
  const groupedByMonth = visibleRecords.reduce((acc, r) => {
    const key = r.date ? [r.date.split('-')[0], r.date.split('-')[2]].join('-') : 'Unknown';
    if (!acc[key]) acc[key] = { redmond: [], kirkland: [] };
    acc[key][r.resolvedLocation].push(r);
    return acc;
  }, {});

  const monthKeys = Object.keys(groupedByMonth).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    const [am, ay] = a.split('-').map(Number);
    const [bm, by] = b.split('-').map(Number);
    return ay - by || am - bm;
  });

  const monthDisplayLabel = (key) => {
    if (key === 'Unknown') return 'Unknown Date';
    const [m, y] = key.split('-').map(Number);
    return monthLabel(y, m);
  };

  const renderLocationTable = (locKey, rows) => {
    if (rows.length === 0) return null;
    return (
      <div key={locKey} style={{ marginBottom: '0.85rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
          <span style={{
            fontSize: '0.78rem', fontWeight: '700',
            color: locKey === 'kirkland' ? '#7c3aed' : '#1e40af',
            backgroundColor: locKey === 'kirkland' ? '#f3e8ff' : '#eff6ff',
            padding: '0.2rem 0.6rem', borderRadius: '6px',
          }}>
            📍 {LOCATION_LABELS[locKey]}
          </span>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>{rows.length} checked in</span>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.6rem 0.75rem' }}>Date</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Student</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Grade</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Program</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Check-In</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Check-Out</th>
                <th style={{ padding: '0.6rem 0.75rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{r.date}</td>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{studentDisplayName(r)}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{r.grade || ''}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{r.program || ''}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{formatTime(r.checkInTime)}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{formatTime(r.checkOutTime)}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleExport = async () => {
    if (records.length === 0) return;
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();

    const columns = [
      { header: 'Date', key: 'date', width: 12 },
      { header: 'Student Name', key: 'name', width: 24 },
      { header: 'Grade', key: 'grade', width: 8 },
      { header: 'Program', key: 'program', width: 26 },
      { header: 'Location', key: 'location', width: 12 },
      { header: 'Check-In Time', key: 'checkIn', width: 14 },
      { header: 'Checked In By', key: 'checkInBy', width: 16 },
      { header: 'Check-Out Time', key: 'checkOut', width: 14 },
      { header: 'Checked Out By', key: 'checkOutBy', width: 16 },
      { header: 'Status', key: 'status', width: 12 },
    ];

    const addSheet = (name, rows) => {
      const sheet = workbook.addWorksheet(name);
      sheet.columns = columns;
      sheet.getRow(1).font = { bold: true };
      rows.forEach((r) => {
        sheet.addRow({
          date: r.date || '',
          name: studentDisplayName(r),
          grade: r.grade || '',
          program: r.program || '',
          location: LOCATION_LABELS[r.resolvedLocation] || '',
          checkIn: formatTime(r.checkInTime),
          checkInBy: r.checkInBy || '',
          checkOut: formatTime(r.checkOutTime),
          checkOutBy: r.checkOutBy || '',
          status: r.status || '',
        });
      });
    };

    addSheet('All Locations', records);
    addSheet('Redmond', records.filter((r) => r.resolvedLocation === 'redmond'));
    addSheet('Kirkland', records.filter((r) => r.resolvedLocation === 'kirkland'));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance_Report_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <h1 style={{ fontSize: '1.4rem', fontWeight: '800', margin: '1rem 0 0.25rem' }}>
          Attendance Report
        </h1>
        <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9rem' }}>
          Total students checked in, by date range and location
        </p>
      </div>

      {/* Filters */}
      <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: '600' }}>
            <input
              type="checkbox"
              checked={useCustomRange}
              onChange={(e) => setUseCustomRange(e.target.checked)}
            />
            Use custom date range instead
          </label>
        </div>

        {!useCustomRange ? (
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            {PRESET_MONTHS.map((m, i) => (
              <button
                key={m.label}
                onClick={() => toggleMonth(i)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '7px',
                  border: selectedMonths[i] ? '1.5px solid #3b82f6' : '1.5px solid #d1d5db',
                  backgroundColor: selectedMonths[i] ? '#eff6ff' : 'white',
                  color: selectedMonths[i] ? '#1e40af' : '#6b7280',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600' }}>
              From{' '}
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', marginLeft: '0.4rem' }}
              />
            </label>
            <label style={{ fontSize: '0.85rem', fontWeight: '600' }}>
              To{' '}
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', marginLeft: '0.4rem' }}
              />
            </label>
          </div>
        )}

        {/* Location filter */}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Location:</span>
          {[
            { id: 'all', label: 'All Locations' },
            { id: 'redmond', label: 'Redmond' },
            { id: 'kirkland', label: 'Kirkland' },
          ].map((loc) => (
            <button
              key={loc.id}
              onClick={() => setLocationFilter(loc.id)}
              style={{
                padding: '0.4rem 0.85rem',
                borderRadius: '7px',
                border: locationFilter === loc.id ? '1.5px solid #6366f1' : '1.5px solid #d1d5db',
                backgroundColor: locationFilter === loc.id ? '#eef2ff' : 'white',
                color: locationFilter === loc.id ? '#4338ca' : '#6b7280',
                fontWeight: '700',
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              {loc.label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.6rem' }}>
          <button
            onClick={runReport}
            disabled={loading}
            style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: loading ? 0.6 : 1 }}
          >
            <Search size={16} /> {loading ? 'Loading...' : 'Run Report'}
          </button>
          {records.length > 0 && (
            <button
              onClick={handleExport}
              style={{ ...styles.button, backgroundColor: '#059669', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <Download size={16} /> Export to Excel
            </button>
          )}
        </div>

        {error && <div style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.85rem', fontWeight: '600' }}>{error}</div>}
      </div>

      {/* Results */}
      {hasSearched && !loading && (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
              Total checked in: {visibleRecords.length}
            </div>
            {locationFilter === 'all' && (
              <div style={{ display: 'flex', gap: '0.6rem', fontSize: '0.85rem', color: '#6b7280', fontWeight: '600' }}>
                <span>· Redmond: {totalsByLocation.redmond}</span>
                <span>· Kirkland: {totalsByLocation.kirkland}</span>
              </div>
            )}
          </div>

          {monthKeys.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', backgroundColor: 'white', borderRadius: '10px' }}>
              No check-in records found for the selected range.
            </div>
          ) : (
            monthKeys.map((key) => (
              <div key={key} style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: '700', margin: '0 0 0.6rem' }}>{monthDisplayLabel(key)}</h2>
                {renderLocationTable('redmond', groupedByMonth[key].redmond)}
                {renderLocationTable('kirkland', groupedByMonth[key].kirkland)}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default AttendanceReport;
